'use client';

import { useInView } from 'react-intersection-observer';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { BrandListingItemViewModel } from '@/types/api';
import { getBaseURL } from '@/features/navigation/utils/url-helpers';
import { AppNextLink } from '@/features/app-next-link';
import { useDrawer } from '@/features/drawer/context/use-drawer';
import { TextField } from '@/features/form';
import { useGetMenuBrands } from '@/components/header/utils/use-fetch-brands';
import { IconClose, IconSearchNormal } from '@/components/svg-symbols/icons';
import { PLPPages } from '@/app/(plp)/types';
import SearchBrandResult from './search-brand-result';
import TopBrands from './top-brands';
import { PAGE_SIZE_OF_BRANDS } from '../../constants';
import { HeaderDrawerName } from '../../constants';
import { closeMenuOnNavigate } from '../../utils/close-menu-on-navigate';
import getBrandAlphabet from '../../utils/get-brand-alphabet';
import SearchSkeleton from '../search-skeleton';
import Title from '../title';
interface Props {
  type: 'mobile' | 'desktop';
}
function AllBrands({
  type
}: Props) {
  const [query, setQuery] = useState('');
  const {
    ref,
    inView
  } = useInView();
  const {
    ref: searchBrandRef,
    inView: inViewBrand
  } = useInView();
  const brandsRef = useRef<BrandListingItemViewModel[]>([]);
  const {
    isDrawerOpen
  } = useDrawer();
  const isMobileDrawerOpen = isDrawerOpen(HeaderDrawerName);
  const debouncedQuery = useDebounce(query, 300);
  const onSearchBoxChangeHandler = (value: string) => {
    setQuery(value);
  };

  // Only fetch brands in mobile view when drawer is open, always fetch on desktop
  const shouldFetchBrands = type === 'desktop' || isMobileDrawerOpen;
  const {
    data: infinitBrands,
    fetchNextPage,
    isFetching
  } = useGetMenuBrands(debouncedQuery, shouldFetchBrands);
  const filteredBrands = useMemo(() => infinitBrands?.pages.reduce((acc: BrandListingItemViewModel[], cur) => {
    const newItems = cur.items || [];
    return [...acc, ...newItems];
  }, []) || [], [infinitBrands]);
  if (!query) {
    brandsRef.current = filteredBrands;
  }
  const {
    totalCount,
    pageNumber: lastPageNumber = 1
  } = infinitBrands?.pages.at(0) || {};
  const pageCount = totalCount ? Math.ceil(totalCount / PAGE_SIZE_OF_BRANDS) : 0;
  useEffect(() => {
    // when the user scroll the carousel
    if (!isFetching && inView && lastPageNumber < pageCount) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isFetching, lastPageNumber, pageCount]);
  if (!inViewBrand && query.length) {
    setQuery('');
  }
  return <div className={classNames('flex flex-col gap-0', {
    'min-h-0': type === 'desktop'
  })} ref={searchBrandRef} data-sentry-component="AllBrands" data-sentry-source-file="all-brands.tsx">
            {type === 'desktop' && <div className='mt-1 shrink-0 md:mb-4 md:mt-0 md:min-w-[363px]'>
                    <TextField value={query} onChange={e => {
        onSearchBoxChangeHandler(e.target.value.trimStart());
      }} placeholder='جست‌وجوی برند' inputSize='large' id='brand-search' rightIcon={!query ? <IconSearchNormal className='h-6 w-6 !text-text-mediumGray' /> : <IconClose className='h-6 w-6 cursor-pointer !text-text-mediumGray' onClick={() => setQuery('')} />} spaceBottomStyle='hidden' className='md:ml-3' />
                </div>}
            <div className='md:min-h-0 md:flex-1 md:overflow-y-auto'>
                {isFetching && type === 'desktop' ? <SearchSkeleton className='mt-2 lg:mt-0' /> : <>
                        {!filteredBrands.length && !!query.length && <div className='mb-8 mt-6 text-body-14 font-normal text-text-mediumGray lg:mt-0'>
                                برای جست‌وجوی شما نتیجه‌ای یافته نشد!
                            </div>}

                        {!!filteredBrands.length && query.length > 1 && <SearchBrandResult brands={filteredBrands} />}
                        <TopBrands mode='mobile' />
                    </>}

                <div className={classNames('hidden-scrollbr mt-4 border-border-gray-medium md:mt-0 md:border-l', {
        'hidden lg:block': isFetching && query.length
      })}>
                    <Title text='همه برندها (ا - ی)' data-sentry-element="Title" data-sentry-source-file="all-brands.tsx" />

                    <ul className='mt-2 pr-2 text-body-14 font-normal md:mt-4 md:pr-2.5'>
                        {brandsRef.current.map(({
            id,
            slug,
            nameFa,
            nameEn
          }, index) => {
            const brandAlphabet = getBrandAlphabet(nameFa || '', brandsRef.current[index - 1]?.nameFa);
            return <Fragment key={id}>
                                    {brandAlphabet.alphabet && <span id={`alphabet-${brandAlphabet.index}`} className='mb-1 mt-2 block w-11 rounded-l-sm bg-brand-secondary text-center leading-6 text-white'>
                                            {brandAlphabet.alphabet}
                                        </span>}

                                    <li>
                                        <AppNextLink href={`/${getBaseURL({
                  page: PLPPages.Brands,
                  slug: slug || ''
                })}`} onClick={() => closeMenuOnNavigate()} className='block py-1 text-body-14 font-normal leading-6 hover:text-brand-secondary' prefetch={false}>
                                            <span className='text-text-darkGray'>{`${nameFa} `}</span>
                                            <span className='text-text-mediumGray'>({nameEn})</span>
                                        </AppNextLink>
                                    </li>
                                </Fragment>;
          })}
                        {!isFetching && <div className='h-5 w-full' ref={ref} />}
                    </ul>
                </div>
            </div>
        </div>;
}
export default AllBrands;